import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FacebookShareButton } from 'react-share';
import ReactTooltip from 'react-tooltip';
import { Link } from 'react-router-dom';

import PhotoGridViewerActionButton from '../../../../../../../components/grid/components/PhotoGridViewerActionButton';
import useBasename from '../../../../../../../components/routing/useBasename';
import FeatureBlock from '../../../../../../../components/contextual/FeatureBlock';
import {
  canAccessSpotlightOnBasename,
  canAccessOriginalDownloadsOnBasename,
} from '../../../../../../../util/product-permissions';
import DownloadIcon from '../../../../../../../components/icons/DownloadIcon';
import PhotosIcon from '../../../../../../../components/icons/PhotosIcon';
import TrashIcon from '../../../../../../../components/icons/TrashIcon';
import TagIcon from '../../../../../../../components/icons/TagIcon';
import SpotlightIcon from '../../../../../../../components/icons/SpotlightIcon';
import FaceMatchingIcon from '../../../../../../../components/icons/FaceMatchingIcon';
import FacebookSquareIcon from '../../../../../../../components/icons/FacebookSquareIcon';
import { NoContributorPermissionBlock } from '../../../../../services/contributors';
import useOpenModal from '../../../../../../../hooks/useOpenModal';
import { PHOTO_ALBUM_FOLDER_TAGS } from '../../../../../../../constants/modals';
import usePhotoDownload from '../../../../../../../hooks/usePhotoDownload';
import useContributorPermission from '../../../../../services/contributors/hooks/useContributorPermission';
import getConfig from '../../../../../../../util/config';
import Button from '../../../../../../../components/global/Button';
import { removeHashes } from '../../../../../../../util/text';
import CoverPhotoButton from '../../../AlbumPhotoViewer/CoverPhotoButton';
import PhotoForFacesHighlightModal from '../../../../../components/FaceHighlightsPhotoGrid/PhotoForFacesHighlight/modal';
import FaceTagPhotoModal from '../../../../FaceTagPhoto/modal';
import PublishIcon from '../../../../../../../components/icons/PublishIcon';
import {
  ALBUM_TYPE,
  ALBUM_PHOTO_STATUS,
} from '../../../../../../../constants/albums';
import EyeSlashIcon from '../../../../../../../components/icons/EyeSlashIcon';
import { isMobile } from '../../../../../../../util/device-detector';
import useRequestConfirm from '../../../../../../../hooks/useRequestConfirm';
import ShoppingBasketIcon from '../../../../../../../components/icons/ShoppingBasketIcon';
import FavoritesIcon from '../../../../../../../components/icons/FavoritesIcon';
import FavoritesEmptyIcon from '../../../../../../../components/icons/FavoritesEmptyIcon';
import SlideshowIcon from '../../../../../../../components/icons/SlideshowIcon';
import CheckmarkIcon from '../../../../../../../components/icons/CheckmarkIcon';
import { WEDDING_EVENT_FEATURE_UPSELL_MODAL } from '../../../../AlbumFolder/WeddingEvents/WeddingEventFeatureUpsell';
import {
  FEATURE_PERMISSION_TYPE,
  FeaturePermissionBlock,
} from '../../../../../services/featurePermissions';
import FreemiumIconWrap from '../../../../../../../components/icons/FreemiumIconWrap';
import { SEARCH_SUBSCRIPTION_FEATURE_UPSELL_MODAL } from '../../../../../../temp/views/onboarding/PhotoSearchUpsell/components/SearchSubscriptionFeatureUpsell';
import { WALDOFICATION_SIMPLIFIED_UPSELL_MODAL } from '../../../../../../../components/global/WaldoficationSimplifiedUpsell';
import {
  PURCHASED_EVENT_FEATURES,
  PURCHASED_EVENT_ADDON_FEATURES,
  SEARCH_SUBSCRIPTION_FEATURES,
} from '../../../../../services/featurePermissions/helpers';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import DotTrigger from '../../../../../../../components/global/DotTrigger';
import PostcardIcon from '../../../../../../../components/icons/PostcardIcon';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onDelete: PropTypes.func,
  onVisibilityToggle: PropTypes.func,
  onFavoriteToggle: PropTypes.func,
  onPhotoItemPurchase: PropTypes.func,
  onWaldogramPurchase: PropTypes.func,
  isFavorite: PropTypes.bool,
  ignorePhotoStatus: PropTypes.bool,
  hasSlideshow: PropTypes.bool,
  onSlideshowToggle: PropTypes.func,
  isDropDown: PropTypes.bool,
  onPrevRecord: PropTypes.func,
  onNextRecord: PropTypes.func,
  prevRecordId: PropTypes.string,
  nextRecordId: PropTypes.string,
  downloadEnabled: PropTypes.bool,
  greenScreenConfigurationEnabled: PropTypes.bool,
  identityId: PropTypes.string,
};

const ShutterbugPhotoGroupPhotoViewerActions = ({
  album,
  photo,
  onDelete,
  onVisibilityToggle,
  onFavoriteToggle,
  onPhotoItemPurchase,
  onWaldogramPurchase,
  isFavorite,
  ignorePhotoStatus,
  hasSlideshow,
  onSlideshowToggle,
  albumFolderId: propsAlbumFolderId,
  isDropDown,
  onPrevRecord,
  onNextRecord,
  prevRecordId,
  nextRecordId,
  downloadEnabled = true,
  greenScreenConfigurationEnabled = false,
  identityId,
}) => {
  const basename = useBasename();
  const openModal = useOpenModal();
  const requestConfirm = useRequestConfirm();
  const { handleDownload, loading: loadingDownload } = usePhotoDownload({
    photoId: photo.id,
  });
  const { isContributor } = useContributorPermission({
    entityId: album?.id,
  });
  const allowOriginalDownload =
    canAccessOriginalDownloadsOnBasename(basename) || isContributor;
  const [highlightsMode, setHighlightsMode] = useState(false);
  const [faceTagMode, setFaceTagMode] = useState(false);
  const isPublished =
    photo?.album_photo_status === ALBUM_PHOTO_STATUS.PUBLISHED ||
    ignorePhotoStatus;

  const albumFolderId = propsAlbumFolderId ?? album?.root_album_folder?.id;
  const DropDownWrapper = ({ children }) =>
    isDropDown ? (
      <>{children}</>
    ) : (
      <>
        <div className="dropdown-menu-dark">
          <DropdownMenu
            toggle={<DotTrigger className="is-overlay" dotType="vertical" />}
            placement="bottom-end"
            closeOnClickWithin
          >
            {children}
          </DropdownMenu>
        </div>
      </>
    );

  return (
    <Fragment>
      <NoContributorPermissionBlock entityId={album.id}>
        {isPublished && (
          <PhotoGridViewerActionButton
            label="Face Tag"
            icon={<FaceMatchingIcon />}
            onClick={() => setFaceTagMode(true)}
          />
        )}
        <FaceTagPhotoModal
          forceOpen={faceTagMode}
          photoId={photo.id}
          albumId={album.id}
          onClose={() => setFaceTagMode(false)}
          onPrevRecord={onPrevRecord}
          onNextRecord={onNextRecord}
          prevRecordId={prevRecordId}
          nextRecordId={nextRecordId}
        />
        <FeatureBlock condition={canAccessSpotlightOnBasename(basename)}>
          {albumFolderId && (
            <FeaturePermissionBlock
              albumFolderId={albumFolderId}
              purchasedEventFeatures={[
                PURCHASED_EVENT_FEATURES.face_highlights_enabled,
              ]}
              searchSubscriptionFeatures={[
                SEARCH_SUBSCRIPTION_FEATURES.face_highlights_enabled,
              ]}
              purchasedEventAddonFeatures={[
                PURCHASED_EVENT_ADDON_FEATURES.face_highlights_enabled,
              ]}
              fallbackContent={featurePermissionItem => (
                <PhotoGridViewerActionButton
                  label="Spotlight"
                  icon={
                    <FreemiumIconWrap>
                      <SpotlightIcon />
                    </FreemiumIconWrap>
                  }
                  onClick={() => {
                    const modalType = (() => {
                      if (
                        featurePermissionItem.type ===
                        FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_FEATURES
                      ) {
                        return WEDDING_EVENT_FEATURE_UPSELL_MODAL;
                      }

                      if (
                        featurePermissionItem.type ===
                        FEATURE_PERMISSION_TYPE.SEARCH_SUBSCRIPTION_FEATURE
                      ) {
                        return SEARCH_SUBSCRIPTION_FEATURE_UPSELL_MODAL;
                      }

                      if (
                        featurePermissionItem.type ===
                        FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES
                      ) {
                        return WALDOFICATION_SIMPLIFIED_UPSELL_MODAL;
                      }

                      return null;
                    })();

                    if (modalType) {
                      openModal(modalType, {
                        albumId: album?.id,
                        albumFolderId,
                        featureId:
                          SEARCH_SUBSCRIPTION_FEATURES.face_highlights_enabled,
                        onCheckoutSuccess: () => {
                          setFaceTagMode(true);
                        },
                      });
                    }
                  }}
                />
              )}
            >
              <PhotoGridViewerActionButton
                label="Spotlight"
                icon={<SpotlightIcon />}
                onClick={() => setHighlightsMode(true)}
              />
            </FeaturePermissionBlock>
          )}
          <PhotoForFacesHighlightModal
            forceOpen={highlightsMode}
            photoRecord={photo}
            albumId={album.id}
            onClose={() => setHighlightsMode(false)}
          />
        </FeatureBlock>
        {albumFolderId && album.album_type !== ALBUM_TYPE.EVENT && (
          <FeaturePermissionBlock
            albumFolderId={albumFolderId}
            purchasedEventFeatures={[
              PURCHASED_EVENT_FEATURES.custom_tags_enabled,
            ]}
            searchSubscriptionFeatures={[
              SEARCH_SUBSCRIPTION_FEATURES.custom_tags_enabled,
            ]}
            purchasedEventAddonFeatures={[
              PURCHASED_EVENT_ADDON_FEATURES.custom_tags_enabled,
            ]}
            fallbackContent={featurePermissionItem => {
              return (
                <PhotoGridViewerActionButton
                  label="Custom Tags"
                  icon={
                    <FreemiumIconWrap>
                      <TagIcon />
                    </FreemiumIconWrap>
                  }
                  onClick={() => {
                    const modalType = (() => {
                      if (
                        featurePermissionItem.type ===
                        FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_FEATURES
                      ) {
                        return WEDDING_EVENT_FEATURE_UPSELL_MODAL;
                      }

                      if (
                        featurePermissionItem.type ===
                        FEATURE_PERMISSION_TYPE.SEARCH_SUBSCRIPTION_FEATURE
                      ) {
                        return SEARCH_SUBSCRIPTION_FEATURE_UPSELL_MODAL;
                      }

                      if (
                        featurePermissionItem.type ===
                        FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES
                      ) {
                        return WALDOFICATION_SIMPLIFIED_UPSELL_MODAL;
                      }

                      return null;
                    })();

                    if (modalType) {
                      openModal(modalType, {
                        albumId: album?.id,
                        albumFolderId: album?.root_album_folder?.id,
                        featureId:
                          SEARCH_SUBSCRIPTION_FEATURES.custom_tags_enabled,
                        onCheckoutSuccess: () => {
                          openModal(PHOTO_ALBUM_FOLDER_TAGS, {
                            photoId: photo.id,
                            albumId: album.id,
                          });
                        },
                      });
                    }
                  }}
                />
              );
            }}
          >
            <PhotoGridViewerActionButton
              label="Custom Tags"
              icon={<TagIcon />}
              onClick={() =>
                openModal(PHOTO_ALBUM_FOLDER_TAGS, {
                  photoId: photo.id,
                  albumId: album.id,
                })
              }
            />
          </FeaturePermissionBlock>
        )}
      </NoContributorPermissionBlock>

      {downloadEnabled &&
        (allowOriginalDownload ? (
          <div className="dropdown-menu-dark">
            <DropdownMenu
              toggle={
                <PhotoGridViewerActionButton
                  label="Download Photo"
                  icon={<DownloadIcon />}
                  disabled={loadingDownload}
                  className={classNames({
                    'is-loading': loadingDownload,
                  })}
                />
              }
            >
              <Button
                btnStyle="reset"
                className="dropdown-item h-auto-i"
                disabled={loadingDownload}
                onClick={() => handleDownload()}
              >
                <p className="mb-0">
                  Download hi-res
                  <br />
                  <span className="text-light text-xs">
                    Great for prints up to 8x10
                  </span>
                </p>
              </Button>
              <Button
                btnStyle="reset"
                className="dropdown-item h-auto-i"
                disabled={loadingDownload}
                onClick={() => handleDownload({ downloadOriginal: true })}
              >
                <p className="mb-0">
                  Download original
                  <br />
                  <span className="text-light text-xs">
                    Great for large format printing
                  </span>
                </p>
              </Button>
            </DropdownMenu>
          </div>
        ) : (
          <PhotoGridViewerActionButton
            label="Download Hi-res"
            icon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={loadingDownload}
            className={classNames({
              'is-loading': loadingDownload,
            })}
          />
        ))}

      {identityId &&
        greenScreenConfigurationEnabled &&
        album?.root_album_folder?.picture_day_enabled && (
          <PhotoGridViewerActionButton
            label="Configure Download"
            icon={<DownloadIcon />}
          >
            {({
              className: gridButtonClasses,
              isMobile: gridButtonIsMobile,
            }) => (
              <Link
                className={gridButtonClasses}
                data-tip="Configure Download"
                data-for="Configure Download"
                to={`/cards/downloads-configurator/${album.root_album_folder.id}?photoIds[]=${photo.id}&identityId=${identityId}&source=identities`}
              >
                <DownloadIcon />
                <span className={gridButtonIsMobile ? 'ml-1' : 'sr-only'}>
                  Configure Download
                </span>
                {!gridButtonIsMobile && (
                  <ReactTooltip id="Configure Download" className="tooltip" />
                )}
              </Link>
            )}
          </PhotoGridViewerActionButton>
        )}

      {onPhotoItemPurchase && photo && (
        <PhotoGridViewerActionButton
          label="Buy prints & more..."
          icon={<ShoppingBasketIcon />}
          onClick={onPhotoItemPurchase}
        />
      )}
      {onWaldogramPurchase && photo && (
        <PhotoGridViewerActionButton
          label="Send a Waldogram"
          icon={<PostcardIcon color="#ffffff" />}
          onClick={onWaldogramPurchase}
        />
      )}

      {isPublished && hasSlideshow && (
        <NoContributorPermissionBlock entityId={album.id}>
          <PhotoGridViewerActionButton
            label={
              photo.is_album_slideshow_photo
                ? 'Remove from Slideshow'
                : 'Add to Slideshow'
            }
            icon={
              photo.is_album_slideshow_photo ? (
                <div className="checked-slideshow">
                  <SlideshowIcon />
                  <div>
                    <CheckmarkIcon size={7} className="slideshow-checkmark" />
                  </div>
                </div>
              ) : (
                <SlideshowIcon />
              )
            }
            onClick={() => onSlideshowToggle(photo)}
          />
        </NoContributorPermissionBlock>
      )}

      {onFavoriteToggle && photo && (
        <PhotoGridViewerActionButton
          label={isFavorite ? 'Remove Favorite' : 'Favorite Photo'}
          icon={isFavorite ? <FavoritesIcon /> : <FavoritesEmptyIcon />}
          onClick={onFavoriteToggle}
        />
      )}

      {isPublished && (
        <PhotoGridViewerActionButton>
          {({ isMobile: isMobileResolution, className }) => (
            <FacebookShareButton
              className={className}
              url={`${getConfig().app.baseUrl}/p/${photo?.id}`}
              hashtag={album?.hash_code && `#${removeHashes(album?.hash_code)}`}
              data-tip="Share on Facebook"
              data-for="facebook-share-button"
            >
              <FacebookSquareIcon />
              {isMobileResolution && (
                <span className="ml-1">Share on Facebook</span>
              )}
              {!isMobileResolution && (
                <ReactTooltip id="facebook-share-button" className="tooltip" />
              )}
            </FacebookShareButton>
          )}
        </PhotoGridViewerActionButton>
      )}

      <NoContributorPermissionBlock entityId={album.id}>
        {/* Only allow visibility toggling if these were pre-hidden photos */}
        {onVisibilityToggle && photo && (
          <PhotoGridViewerActionButton
            label={isPublished ? 'Hide Photo' : 'Publish Photo'}
            icon={isPublished ? <EyeSlashIcon /> : <PublishIcon />}
            onClick={() => {
              if (isMobile()) {
                requestConfirm({
                  message: (
                    <>
                      <h3 className="text-center">
                        Are you sure you want to{' '}
                        {isPublished ? 'hide' : 'publish'} this photo?
                      </h3>
                    </>
                  ),
                  confirmText: isPublished ? 'Hide' : 'Publish',
                  cancelText: 'Go back',
                  onConfirm: onVisibilityToggle,
                  onCancel: () => {},
                });
              } else {
                onVisibilityToggle();
              }
            }}
          />
        )}
        <DropDownWrapper>
          {isPublished && (
            <NoContributorPermissionBlock entityId={album.id}>
              <CoverPhotoButton photoId={photo.id} album={album} skipQueries>
                {({ updateCoverPhoto, loading }) => {
                  return (
                    <PhotoGridViewerActionButton
                      label="Use as cover photo"
                      icon={<PhotosIcon />}
                      onClick={updateCoverPhoto}
                      disabled={loading}
                      forceMobileMode={!isDropDown}
                    />
                  );
                }}
              </CoverPhotoButton>
            </NoContributorPermissionBlock>
          )}

          {onDelete && (
            <PhotoGridViewerActionButton
              label="Delete"
              icon={<TrashIcon />}
              onClick={onDelete}
              forceMobileMode={!isDropDown}
            />
          )}
        </DropDownWrapper>
      </NoContributorPermissionBlock>
    </Fragment>
  );
};

ShutterbugPhotoGroupPhotoViewerActions.propTypes = propTypes;

export default ShutterbugPhotoGroupPhotoViewerActions;
